<template>
  <modal
    :focus-trap="true"
    :scrollable="false"
    :draggable="true"
    :adaptive="true"
    :reset="true"
    :click-to-close="false"
    :shift-x="0.98"
    :shift-y="0.7"
    width="500px"
    height="auto"
    name="video-modal"
    class="video-call-widget"
    styles="pointer-events: auto !important;"
    @opened="maybeLoadStreams"
    @closed="onModalClosed"
  >
    <div class="video-call-center-container">
      <div>
        <h5 class="text-center">Appointment with {{ getValueFromSource(callee, 'full_name') }}</h5>
        <h6 class="text-center text-sm text-dark" style="font-size: 13px;">{{ formatTime(seconds) }}</h6>
      </div>

      <div class="videos">
        <div class="video-call-container">
          <video 
            v-show="remoteVideoIsOn" 
            id="remote-video" 
            ref="remoteVideo"
            autoplay 
            playsinline
          />

          <div v-if="!remoteVideoIsOn" class="no-video">
            <p v-if="remoteMicIsOn">Patient has turned off camera</p>
            <p v-else>Patient has turned off camera and muted</p>
            <feather-icon icon="VideoOffIcon" size="50"></feather-icon>
          </div>

          <div v-if="remoteVideoIsOn" class="controls">
            <feather-icon size="17" :icon="remoteMicIcon" class="text-muted cursor-pointer" />
            <feather-icon size="17" :icon="remoteVideoIcon" class="text-muted cursor-pointer" />
          </div>
          
          <video 
            v-show="localVideoIsOn" 
            id="local-video" 
            ref="localVideo"
            autoplay 
            playsinline
          />
          <div v-if="!localVideoIsOn" class="no-local-video">
            <feather-icon icon="CameraOffIcon" size="30"></feather-icon>
          </div>
        </div>
      </div>

      <div class="controls">
        <b-button variant="success" pill @click="toggleMicIcon">
          <feather-icon color="white" :icon="micIcon" />
        </b-button>

        <b-button size="lg" variant="danger" class="d-flex align-items-center" pill @click="endVideoCall">
          <feather-icon color="white" icon="PhoneOffIcon" class="" />
        </b-button>

        <b-button variant="success" pill @click="toggleVideoIcon">
          <feather-icon color="white" :icon="videoIcon" />
        </b-button>
      </div>
    </div>
  </modal>
</template>

<script>
import { BButton } from "bootstrap-vue";

import webRTCBus from '@core/mixins/bus/webrtc';
import WEB_RTC_ACTIONS from '@core/utils/webrtc';

export default {
  name: 'VideoModal',
  components: { BButton },
  data() {
    return {
      seconds: 0,
      timer: 0,

      micIcon: "MicIcon",
      videoIcon: "VideoIcon",

      remoteMicState: 'ON',
      remoteVideoState: 'ON',
    }
  },
  computed: {
    callee() {
      return this.$store.getters[`webrtc/callee`]
    },

    localStream() {
      return this.$store.getters[`webrtc/localStream`]
    },
    remoteStream() {
      return this.$store.getters[`webrtc/remoteStream`]
    },

    isStreaming() {
      return this.localStream && this.remoteStream
    },

    localVideoIsOn() {
      return this.videoIcon === 'VideoIcon';
    },
    remoteCallEvent() {
      return this.$store.getters[`webrtc/remoteCallEvent`]
    },
    remoteMicIsOn() {
      return this.remoteMicState === 'ON';
    },
    remoteVideoIsOn() {
      return this.remoteVideoState === 'ON';
    },
    remoteMicIcon() {
      return this.remoteMicIsOn ? "MicIcon" : "MicOffIcon"
    },
    remoteVideoIcon() {
      return this.remoteVideoIsOn ? "VideoIcon" : "VideoOffIcon"
    }
  },
  watch: {
    remoteCallEvent: {
      handler(event) {
        console.log('REMOTE CALL EVENT', event)
        switch (event) {
          case "MIC_ON":
              this.remoteMicState = 'ON'
            break;

          case "MIC_OFF":
              this.remoteMicState = 'OFF';
            break;

          case "VIDEO_ON":
              this.remoteVideoState = 'ON';
            break;

          case "VIDEO_OFF":
              this.remoteVideoState = 'OFF';
            break;
          
          default:
            break
        }
      },
      immediate: false
    },
    isStreaming: {
      handler(streaming){
        console.log('IS STREAMING')
        if (streaming){
          clearTimeout(this.timer);
          this.seconds = 0;

          this.timer = setInterval(() => {
            this.seconds += 1;
          }, 1000);
        } else if (this.timer){
          clearTimeout(this.timer);
          this.seconds = 0;
        }
      } 
    },
    localStream: {
      handler(newStream) {
        if (newStream && this.$refs.localVideo) {
          console.log('LOAD LOCAL STREAM')
          this.$refs.localVideo.srcObject = newStream;
        }
      },
      immediate: false,
      deep: true
    },
    remoteStream: {
      handler(newStream) {
        if (newStream && this.$refs.remoteVideo) {
          console.log('LOAD REMOTE STREAM')
          this.$refs.remoteVideo.srcObject = newStream;
        }
      },
      immediate: false,
      deep: true
    },
  },
  methods: {
    toggleMicIcon() {
      try {
        const micOn = this.micIcon === "MicIcon"
        const newIcon = micOn ? "MicOffIcon" : "MicIcon"

        this.micIcon = newIcon

        webRTCBus.$emit(WEB_RTC_ACTIONS.LOG_CALL_EVENT, micOn ? 'MIC_OFF' : 'MIC_ON');
        webRTCBus.$emit(WEB_RTC_ACTIONS.TOGGLE_AUDIO, !micOn);
      } catch (error){
        console.log("Error toggleMicIcon", error.message)
      }
    },
    toggleVideoIcon() {
      const videoOn = this.videoIcon === "VideoIcon"
      const newIcon = videoOn ? "VideoOffIcon" : "VideoIcon"

      this.videoIcon = newIcon

      webRTCBus.$emit(WEB_RTC_ACTIONS.LOG_CALL_EVENT, videoOn ? 'VIDEO_OFF' : 'VIDEO_ON');
      webRTCBus.$emit(WEB_RTC_ACTIONS.TOGGLE_VIDEO, !videoOn);
    },
    endVideoCall() {
      webRTCBus.$emit(WEB_RTC_ACTIONS.END_CALL)
    },
    onModalClosed() {
      this.micIcon = "MicIcon";
      this.videoIcon = "VideoIcon";
    },
    formatTime(totalSeconds) {
      const minutes = Math.floor(totalSeconds / 60);
      const remainingSeconds = totalSeconds % 60;
      return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    },
    maybeLoadStreams() {
      if (this.localStream && this.$refs.localVideo){
        console.log('LOAD LOCAL STREAM 2')
        this.$refs.localVideo.srcObject = this.localStream;
      }

      if (this.remoteStream && this.$refs.remoteVideo){
        console.log('LOAD REMOTE STREAM 2')
        this.$refs.remoteVideo.srcObject = this.remoteStream;
      }
    }
  }
}
</script>

<style scoped>
video {
  background: #2c3e50;
}

#remote-video {
  width: 100%;
  height: 400px;
  object-fit: contain;
}

.videos {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
}

.controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.video-call-center-container {
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  min-height: 350px;
  min-width: 350px;
  padding-bottom: 10px;
  padding-top: 10px;
  z-index: 50000000;

  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

.video-call-container {
  position: relative; /* Enable relative positioning for element stacking */
  width: 100%; /* Allow video to size based on content */
  height: fit-content; /* Allow video to size based on content */
}

.video-call-container .controls {
  position: absolute; /* Make icon absolute relative to container */
  bottom: 10px; /* Position icon 10px from bottom */
  left: 10px; /* Position icon 10px from left */
  color: white; /* Assuming you want a white icon */
  font-size: 2em; /* Adjust font size as needed */
}

.controls {
  padding: 2px;
}

.video-call-container #local-video {
  position: absolute; /* Make icon absolute relative to container */
  bottom: 10px; /* Position icon 10px from bottom */
  right: 0px; /* Position icon 10px from left */
  border: 2px solid #128bc7;
  width: auto;
  height: 100px;
}

.video-call-container .no-local-video {
  position: absolute; /* Make icon absolute relative to container */
  bottom: 10px; /* Position icon 10px from bottom */
  right: 0px; /* Position icon 10px from left */
  border: 2px solid #128bc7;
  background-color: lightgrey;
  width: auto;
  height: 100px;
  width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-video {
  display: flex;
  min-width: 432px;
  min-height: 400px;
  align-items: center;
  flex-direction: column;
  justify-content: center;
}

</style>
